/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Box, CardMedia, Fab, Typography, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Scrollbar, Keyboard, Autoplay, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { yellow } from "@mui/material/colors";
import Markdown from "markdown-to-jsx";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { useRef } from "react";
import { MOBLIE_VIEW_WIDTH } from "../../constants/constants";
import AddCircularIcon from "./AddCircularIcon";

const carouselBox = (matches) => css`
  ${matches && "position: relative;"}
  ${matches && "height: 45vw; min-height: 80vh; max-height: 86vh;"}
`;

const media = (matches) => css`
  ${matches
    ? "height: 45vw; min-height: 80vh; max-height: 86vh;"
    : "min-height: 200px"}
`;

const mediaVideo = (matches) => css`
  width: 100%;
  object-fit: unset;
  ${matches
    ? "height: 45vw; min-height: 80vh; max-height: 86vh;"
    : "min-height: 200px"}
`;

const imgText = (matches) => css`
  color: white;
  display: flex;
  ${matches && "position: absolute;"}
  margin-top: ${matches ? "10%" : "5%"};
  margin-left: ${matches ? "15%" : "5%"};
  ${!matches && "margin-bottom: 10%;"}
  flex-direction: column;
  z-index: 1;
  ${!matches && "width: 120%;"}
`;

const body = (theme, matches) => css`
  font-size: 18px;
  line-height: ${matches ? "35px" : "24px"};
  max-width: ${matches ? "450px" : "70%"};
  ${!matches && "width: 80%;"}
  font-family: ${theme.typography.MBCORPOS};
`;
const titleStyleWrapper = (theme) => css`
  font-family: ${theme.typography.MBCORPOA};
`;
const useStyles = makeStyles({
  fab: {
    bottom: "60px",
    left: "48%",
    background: "transparent",
    boxShadow: "none",
    zIndex: 1,
  },
  learnMore: {
    textDecoration: "none",
    color: "white",
    fontSize: "14px",
    textAlign: "center",
    border: "2px solid white",
    borderRadius: "25px",
    padding: "5px",
    marginTop: "6%",
    width: "160px",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  learnMoreMobile: {
    textDecoration: "none",
    color: "white",
    textAlign: "center",
    border: "2px solid white",
    borderRadius: "25px",
    padding: "5px",
    position: "absolute",
    top: "150px",
    width: "287px",
    fontSize: "16px !important",
    marginTop: "100%",
    left: "50%",
    transform: "translate(-50%)",
  },
});

const CarouselComponent = ({ handleScroll, carouselData }) => {
  const matches = useMediaQuery(MOBLIE_VIEW_WIDTH);
  const myRef = useRef();
  const styles = useStyles();

  const imageExt = [".jpg", ".png", ".svg", ".tiff", ".ico", ".dvu", ".jpeg"];

  const handleViewScroll = () => {
    handleScroll();
  };

  return (
    <Box css={() => carouselBox(matches)}>
      <Swiper
        modules={[Scrollbar, Keyboard, Autoplay, Navigation]}
        grabCursor
        navigation={matches ? { clickable: true } : false}
        autoHeight={true}
        keyboard={{ enabled: true }}
        spaceBetween={0}
        initialSlide={0}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
      >
        {carouselData?.map((item, index) => {
          return (
            <SwiperSlide
              key={index}
              style={
                matches
                  ? null
                  : { display: "flex", flexDirection: "column-reverse" }
              }
            >
              {imageExt.indexOf(item.media?.data?.attributes?.ext) >= 0 ? (
                <>
                  {(item.title || item.descriptiom || item.buttonLink) && (
                    <Box css={() => imgText(matches)}>
                      {item.title && (
                        <Typography
                          p={1}
                          component="span"
                          css={(theme) => titleStyleWrapper(theme)}
                          variant={matches ? "h2" : "h4"}
                        >
                          {item.title}
                        </Typography>
                      )}
                      {item.description && (
                        <Typography
                          p={1}
                          pt={1}
                          css={(theme) => body(theme, matches)}
                          variant="body1"
                        >
                          <Markdown>{item.description || ""}</Markdown>
                        </Typography>
                      )}

                      {matches && item.buttonLink && (
                        <a
                          className={styles.learnMore}
                          href={item.buttonLink}
                          target={item.isExternalLink ? "_blank" : "_self"}
                          rel="noreferrer"
                        >
                          {item?.buttonText}
                        </a>
                      )}
                      {!matches && item.buttonLink && (
                        <AddCircularIcon
                          buttonCss={"margin-left: 0; padding: 6px;"}
                          handleClick={() =>
                            (window.location = item.buttonLink)
                          }
                          buttonText={item.buttonText}
                        />
                      )}
                    </Box>
                  )}

                  {item.media?.data?.attributes?.url && (
                    <CardMedia
                      component="img"
                      css={() => media(matches)}
                      image={
                        process.env.REACT_APP_BASEURL +
                        item.media?.data?.attributes?.url
                      }
                    ></CardMedia>
                  )}
                </>
              ) : (
                <div
                  style={
                    matches
                      ? null
                      : { display: "flex", flexDirection: "column-reverse" }
                  }
                >
                  {(item.title || item.description || item.buttonLink) && (
                    <Box css={() => imgText(matches)}>
                      {item.title && (
                        <Typography
                          p={1}
                          css={(theme) => titleStyleWrapper(theme)}
                          variant={matches ? "h2" : "h4"}
                        >
                          {item.title}
                        </Typography>
                      )}
                      {item.description && (
                        <Typography
                          css={(theme) => body(theme, matches)}
                          p={1}
                          pt={1}
                          variant="body1"
                        >
                          <Markdown>{item.description || ""}</Markdown>
                        </Typography>
                      )}
                      {matches && item.buttonLink && (
                        <a
                          className={styles.learnMore}
                          href={item.buttonLink}
                          target={item.isExternalLink ? "_blank" : "_self"}
                          rel="noreferrer"
                        >
                          {item?.buttonText}
                        </a>
                      )}
                      {!matches && item.buttonLink && (
                        <AddCircularIcon
                          buttonCss={"margin-left: 0; padding: 6px;"}
                          handleClick={() =>
                            (window.location = item.buttonLink)
                          }
                          buttonText={item.buttonText}
                        />
                      )}
                    </Box>
                  )}

                  {/* {!matches && item.buttonLink && (
                    <a
                      className={
                        matches ? styles.learnMore : styles.learnMoreMobile
                      }
                      href={item.buttonLink}
                      target={item.isExternalLink ? "_blank" : "_self"}
                      rel="noreferrer"
                    >
                      {item?.buttonText}
                    </a>
                  )} */}
                  <CardMedia
                    component="video"
                    css={() => mediaVideo(matches)}
                    // autoPlay={autoPlayState}
                    autoPlay
                    muted
                    loop
                    ref={myRef}
                    image={
                      process.env.REACT_APP_BASEURL +
                      item.media?.data?.attributes?.url
                    }
                  />
                </div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
      {matches && (
        <Fab
          onClick={handleViewScroll}
          style={{
            bottom: "70px",
            left: "48%",
            background: "transparent",
            boxShadow: "none",
            zIndex: 1,
          }}
        >
          <KeyboardArrowDownIcon
            style={{ fontSize: "40px" }}
            sx={{ color: yellow[50] }}
          ></KeyboardArrowDownIcon>
        </Fab>
      )}
    </Box>
  );
};

export default CarouselComponent;
